import * as React from "react";
import { useEffect, useState, useMemo } from "react";
import Box from "@mui/material/Box";
import StyledGrid, { FilterChip, StyledTextSearch } from "../../shared/components/StyledGrid";
import { useNavigate } from "react-router-dom";
import { useGetMembersMutation } from "../../services/InsuranceService";
import useFetchWithMsal from "../../utils/useFetchWithMsal";
import { LoadingPanel } from "../../shared/components/LoadingPanel";
import { Typography, Button, IconButton, TextField, InputAdornment, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import BarChartIcon from '@mui/icons-material/BarChart';
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ExportToExcelButton from "../../shared/components/ExportToExcelButton";
import { useDispatch, useSelector } from "react-redux";
import { TableFilter } from "../../components/tableFilter/TableFilter";
import { memberColumns } from "../../constants/TablesColumns";
import { naicMemberRoleCheck } from "../../shared/utils/permissions";
import { setFirstVisitFalse } from "../../store/slices/FirstVisitSlice";
import { setIsActive, setNaicCode, setMemberName } from "../../store/slices/FilterSlice";
// import { LoadingPanel } from "../../shared/components/LoadingPanel";

import {
  BarChart,

} from "@mui/icons-material";

export const MembersTab = () => {
  const dispatch = useDispatch();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [totalCount, setTotalCount] = useState(0);

  const isFirstVisit = useSelector((state) => state.firstVisit.isFirstVisit);


  const roleId = useSelector((state) => state?.user?.roleId);

  const [sortingModel, setSortingModel] = useState();
  const { skip } = useFetchWithMsal();
  const [getMembers, { data, isLoading, error }] = useGetMembersMutation("", { skip });
  const [getMembersForExport] = useGetMembersMutation();
  const navigate = useNavigate();
  const { naicCode, memberName, isActive, merged, suspWith, insolvent } = useSelector(
    (state) => state.filter
  );
  // Unified search term (NAIC Code or Member Name)
  const [searchTerm, setSearchTerm] = useState(naicCode || memberName || "");
  const [debounceTimer, setDebounceTimer] = useState(null);
  // Import menu state
  const [importAnchorEl, setImportAnchorEl] = useState(null);
  const [importSource, setImportSource] = useState(null); // 'lhiga' | 'assessconnect'
  const fileInputRef = React.useRef();
  // Reports menu state
  const [reportsAnchorEl, setReportsAnchorEl] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (!skip) {
      fetchData()
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    sortingModel?.field,
    sortingModel?.sort,
    skip
  ]);

  // On first mount, default to Active members if not set
  useEffect(() => {
    if (!skip && (isActive === null || isActive === undefined)) {
      dispatch(setIsActive("Active"));
      // Trigger fetch explicitly with Active filter
      fetchData({ naicCode, memberName, isActive: "Active" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip]);

  useEffect(() => {
    if (!isLoading && !searchTerm.trim()) {
      if (data?.totalCount !== totalCount) {
        setTotalCount(data?.totalCount);
      }
    }
  }, [isLoading, data?.totalCount, totalCount, searchTerm]);

  const getStatus = (status) => {
    if (status === "Active") return true;
    if (status === "Inactive") return false;
    if (status === "All") return null;
  };

  const fetchData = (value) => {
    const body = {
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      orderBy: sortingModel?.field || "",
      orderByDirection: sortingModel?.sort || "",
      naicCode: value ? value.naicCode : naicCode,
      memberName: value ? value.memberName : memberName,
      isActive: value ? getStatus(value.isActive) : getStatus(isActive),
      // merged / suspWith / insolvent filtered client-side only
    };
    // Debug: log request payload
    console.debug("[MembersTab] getMembers request:", body);
    getMembers(body);
  };

  // Sync external filter changes (e.g., reset) back into local search field
  useEffect(() => {
    const external = naicCode || memberName || "";
    if (external !== searchTerm) {
      setSearchTerm(external);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [naicCode, memberName]);

  // Helper: numeric code detection (NAIC or group code)
  const isLikelyNaic = (term) => /^\d+$/.test(term.trim());

  // Debounce search term; apply to either NAIC or Member name (exclusive OR style)
  useEffect(() => {
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      const trimmed = searchTerm.trim();
      const naicVal = trimmed ? (isLikelyNaic(trimmed) ? trimmed : null) : null;
      const memberVal = trimmed ? (isLikelyNaic(trimmed) ? null : trimmed) : null; // still send only one textual memberName to API
      dispatch(setNaicCode(naicVal));
      dispatch(setMemberName(memberVal));
      if (!skip) fetchData({ naicCode: naicVal, memberName: memberVal, isActive });
    }, 400);
    setDebounceTimer(timer);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handlePaginationModelChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };
  const onAddMemberClick = () => {
    navigate("/createMember");
  };
  const onOpenImportMenu = (e) => {
    setImportAnchorEl(e.currentTarget);
  };
  const onCloseImportMenu = () => setImportAnchorEl(null);
  const onOpenReportsMenu = (e) => setReportsAnchorEl(e.currentTarget);
  const onCloseReportsMenu = () => setReportsAnchorEl(null);
  const openReport = (url, label) => {
    if (!url) {
      // Placeholder until real URL provided
      /* eslint-disable no-console */
      console.warn(`[MembersTab] No URL configured for report: ${label}`);
      /* eslint-enable no-console */
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    onCloseReportsMenu();
  };
  const handleImportChoice = (source) => {
    setImportSource(source);
    onCloseImportMenu();
    // Trigger hidden file input
    if (fileInputRef.current) {
      fileInputRef.current.value = null; // reset
      fileInputRef.current.click();
    }
  };
  const handleFileSelected = (e) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    const file = files[0];
    // Debug log placeholder; actual parsing to be implemented later
    /* eslint-disable no-console */
    console.debug('[MembersTab] Import selected', { source: importSource, fileName: file.name, size: file.size });
    /* eslint-enable no-console */
    // TODO: implement parsing & import logic for CSV/XLSX
  };
  const onApplyFilters = (value) => {
    // If only local (client-side) filters changed, skip API fetch
    // All filters now server-side except client search
    if (true) {
      if (paginationModel.page) {
        setPaginationModel(prev => { return { ...prev, page: 0 } });
      } else {
        !skip && fetchData(value);
      }
    }

    // getClients({
    //   recordNumber: page * rowsPerPage,
    //   offset: rowsPerPage,
    //   status: resetFilter === 'status' ? '' : status,
    //   counselor: resetFilter === 'counselor' ? '' : counselor,
    //   housing: resetFilter === 'housing' ? '' : housing
    // })
  };
  const applyClientFilters = (list, term, localMerged, localSuspWith, localInsolvent) => {
    let result = list;
    const normalized = term.trim().toLowerCase();
    if (normalized) {
      const isDigits = /^\d+$/.test(normalized);
      if (isDigits) {
        result = result.filter((m) => (
          (m.naicCode || "").toString().toLowerCase().includes(normalized) ||
          (m.groupCode || "").toString().toLowerCase().includes(normalized)
        ));
      } else {
        result = result.filter((m) => (
          (m.memberName || "").toLowerCase().includes(normalized) ||
          (m.groupName || "").toLowerCase().includes(normalized)
        ));
      }
    }

    const normalizeFlag = (value) => (value || "").toString().toLowerCase();
    if (localMerged) {
      const expected = normalizeFlag(localMerged);
      result = result.filter((m) => normalizeFlag(m.merged) === expected);
    }
    if (localSuspWith) {
      const expected = normalizeFlag(localSuspWith);
      result = result.filter((m) => normalizeFlag(m.suspWith) === expected);
    }
    if (localInsolvent) {
      const expected = normalizeFlag(localInsolvent);
      result = result.filter((m) => normalizeFlag(m.insolvent) === expected);
    }

    return result;
  };

  // Client-side filtering for merged / suspWith / insolvent + unified search
  const filteredMembers = useMemo(() => {
    return applyClientFilters(
      data?.members || [],
      searchTerm,
      merged,
      suspWith,
      insolvent
    );
  }, [data?.members, searchTerm, merged, suspWith, insolvent]);

  const fetchAllMembersForExport = async () => {
    setIsExporting(true);
    try {
      const pageSize = 500;
      let page = 1;
      let all = [];
      let total = data?.totalCount || 0;

      while (true) {
        const body = {
          page,
          pageSize,
          orderBy: sortingModel?.field || "",
          orderByDirection: sortingModel?.sort || "",
          naicCode,
          memberName,
          isActive: getStatus(isActive),
          merged,
          suspWith,
          insolvent,
        };

        const result = await getMembersForExport(body).unwrap();
        const members = result?.members || [];
        all = all.concat(members);

        total = result?.totalCount ?? total;
        if (members.length < pageSize || (total && all.length >= total)) break;
        page += 1;
      }

      return applyClientFilters(all, searchTerm, merged, suspWith, insolvent);
    } catch (e) {
      /* eslint-disable no-console */
      console.error("[MembersTab] Export fetch failed", e);
      /* eslint-enable no-console */
      return [];
    } finally {
      setIsExporting(false);
    }
  };

  // Debug: log response data changes
  useEffect(() => {
    if (data !== undefined) {
      console.debug("[MembersTab] getMembers response:", data);
      if (Array.isArray(data?.members)) {
        console.debug(
          "[MembersTab] members count:",
          data.members.length,
          "totalCount:",
          data?.totalCount
        );
      }
    }
  }, [data]);

  // Debug: log errors if any
  useEffect(() => {
    if (error) {
      console.error("[MembersTab] getMembers error:", error);
    }
  }, [error]);

  // Update totalCount when client-side filters (other than Active) are applied
  useEffect(() => {
    const clientFiltersApplied = !!(searchTerm.trim() || merged || suspWith || insolvent);
    if (clientFiltersApplied) {
      setTotalCount(filteredMembers.length);
    } else if (!isLoading && data?.totalCount !== undefined) {
      setTotalCount(data.totalCount);
    }
  }, [searchTerm, filteredMembers.length, isLoading, data?.totalCount]);

  return (
    <Box sx={{ height: "100%", width: "100%", margin: "50px auto 0 auto" }}>
      {isLoading && <LoadingPanel />}
      <Box display={"flex"} sx={{ justifyContent: "space-between", marginBottom: "0px" }} mb={3}>
        <Typography variant="h1" color="primary.main" sx={{ marginLeft: "16px" }}>
          Members
        </Typography>
        <Box display={"flex"} gap={2} alignItems="center" >

          {naicMemberRoleCheck(roleId) && <Button onClick={onAddMemberClick} variant="outlined">
            Add Member
          </Button>}
          {naicMemberRoleCheck(roleId) && <Button
            variant="outlined"
            startIcon={<CloudUploadIcon fontSize="small" />}
            onClick={onOpenImportMenu}
          >
            Import
          </Button>}
          {/* Export current page rows to Excel */}
          <ExportToExcelButton
            rows={filteredMembers}
            columns={memberColumns}
            filename="members.xlsx"
            label={isExporting ? "Exporting..." : "Export"}
            getRows={fetchAllMembersForExport}
            disabled={isExporting}
          />
          <Button
            variant="outlined"
            startIcon={<BarChartIcon fontSize="small" />}
            onClick={onOpenReportsMenu}
          >
            Reports
          </Button>
          <TableFilter onApplyFilters={onApplyFilters} type={"members"} isActiveFilter />
          <Menu
            anchorEl={importAnchorEl}
            open={Boolean(importAnchorEl)}
            onClose={onCloseImportMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <MenuItem onClick={() => handleImportChoice('lhiga')}>
              <ListItemIcon><CloudUploadIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="LHIGA Data" secondary="CSV or XLSX" />
            </MenuItem>
            <MenuItem onClick={() => handleImportChoice('assessconnect')}>
              <ListItemIcon><CloudUploadIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="AssessConnect Data" secondary="CSV or XLSX" />
            </MenuItem>
          </Menu>
          <Menu
            anchorEl={reportsAnchorEl}
            open={Boolean(reportsAnchorEl)}
            onClose={onCloseReportsMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <MenuItem onClick={() => openReport('https://app.powerbi.com/groups/me/reports/046a7f9c-8d13-401d-90ca-ad1dcf8a1697/73b4fa8a1f0227f9fc6f?experience=power-bi', 'Members')}>
              <ListItemIcon><BarChartIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Members" />
            </MenuItem>
            <MenuItem onClick={() => openReport('https://app.powerbi.com/groups/me/reports/046a7f9c-8d13-401d-90ca-ad1dcf8a1697/2c2f21dbd0162306590a?experience=power-bi', 'Member Directory')}>
              <ListItemIcon><BarChartIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Directory" />
            </MenuItem>
            <MenuItem onClick={() => openReport('https://app.powerbi.com/groups/me/reports/046a7f9c-8d13-401d-90ca-ad1dcf8a1697/4b71f7cedc7f967592ec?experience=power-bi', 'Member License History')}>
              <ListItemIcon><BarChartIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="License History" />
            </MenuItem>
            <MenuItem onClick={() => openReport('https://app.powerbi.com/groups/me/reports/046a7f9c-8d13-401d-90ca-ad1dcf8a1697/b3fa5e6e089c867b4b2d?experience=power-bi', 'Member Merger History')}>
              <ListItemIcon><BarChartIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Merger History" />
            </MenuItem>
          </Menu>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            accept={".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"}
            onChange={handleFileSelected}
          />
        </Box>
      </Box>
      <Box mb={1} sx={{ display: "flex", justifyContent: "left", flexWrap: "wrap", margin: "10px 0 5px 0", gap: 1 }}>
        <Box mb={2} sx={{ display: "flex", maxWidth: 400, alignItems: "center", }}>
          <StyledTextSearch
            fullWidth
            //variant="outlined"
            //label="Search (NAIC or Member)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")}> <CloseIcon fontSize="inherit" /> </IconButton>
                </InputAdornment>
              ) : null
            }}
            placeholder="Type NAIC/Group code or Member/Group name"
          />
        </Box>
        <Box
        /*           sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    alignItems: "center",
                    marginRight: "15px",
                  }} */
        >
          {isActive && (
            <FilterChip>
              Status: {isActive}
              <IconButton size="small" onClick={() => { dispatch(setIsActive(null)); fetchData({ naicCode, memberName, isActive: null }); }}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </FilterChip>
          )}
          {/* Status-specific chips removed with consolidated status */}
        </Box>
      </Box>
      <StyledGrid
        data={filteredMembers}
        width="100%"
        columns={memberColumns}
        rowCount={totalCount}
        withFilter
        onSortingModelChange={(model) => setSortingModel(model[0])}
        paginationModel={paginationModel}
        setPaginationModel={handlePaginationModelChange}
      // withMenu
      />
    </Box>
  );
};
